import { Component, OnInit } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import {
  CancelInvoicePreview,
  CancelInvoicePreviewGroup,
  CancelInvoicePreviewInvoice,
  CancelInvoicePreviewOrder,
  InvoiceCancelBlockReason,
} from 'src/app/models/dtos/cancel-invoice-preview';

@Component({
  selector: 'app-cancel-batch-dialog',
  templateUrl: './cancel-batch-dialog.component.html',
  styleUrls: ['./cancel-batch-dialog.component.scss'],
})
export class CancelBatchDialogComponent implements OnInit {

  private static readonly CONFIRMATION_WORD = 'confirma';

  private static readonly BLOCK_REASON_LABEL: Record<InvoiceCancelBlockReason, string> = {
    NOT_FOUND: 'Fatura não encontrada nesta marina',
    ALREADY_CANCELED: 'Fatura já está cancelada',
    HAS_ACTIVE_PAYMENT: 'Possui pagamento ativo',
    HAS_ACTIVE_TAX_DOCUMENT: 'Possui nota fiscal ativa',
    HAS_ACTIVE_BILLET: 'Possui boleto não cancelado no banco',
    WITHOUT_ORDERS: 'Fatura sem pedidos',
    QUOTA_GROUP_BLOCKED: 'Rateio compartilhado com fatura impedida de cancelar',
  };

  preview: CancelInvoicePreview;
  cancelableGroups: CancelInvoicePreviewGroup[] = [];
  blockedGroups: CancelInvoicePreviewGroup[] = [];

  frontWarnings: string[] = [];

  confirmationText = '';

  constructor(public config: DynamicDialogConfig, private dialogRef: DynamicDialogRef) {}

  ngOnInit(): void {
    this.preview = this.config.data?.preview;
    this.frontWarnings = this.config.data?.frontWarnings || [];
    const groups = this.preview?.groups || [];
    this.cancelableGroups = groups.filter((g) => g.cancelable);
    this.blockedGroups = groups.filter((g) => !g.cancelable);
  }

  get hasCancelable(): boolean {
    return this.cancelableGroups.length > 0;
  }

  get confirmationEnabled(): boolean {
    return this.hasCancelable
      && this.confirmationText.trim().toLowerCase() === CancelBatchDialogComponent.CONFIRMATION_WORD;
  }

  isQuotaGroup(group: CancelInvoicePreviewGroup): boolean {
    return group.invoices.length > 1;
  }

  cascadeInvoices(group: CancelInvoicePreviewGroup): CancelInvoicePreviewInvoice[] {
    return group.invoices.filter((i) => i.addedByQuotaCascade);
  }

  ordersToRecreate(group: CancelInvoicePreviewGroup): number {
    return group.orders.filter((o) => o.willBeCopied).length;
  }

  get tuitionOrdersCount(): number {
    return this.cancelableGroups
      .reduce((total, group) => total + group.orders.filter((o) => this.isTuition(o)).length, 0);
  }

  get tuitionNoticeText(): string {
    const qtd = this.tuitionOrdersCount;
    return `${qtd} dos pedidos que serão cancelados são de MENSALIDADE e NÃO serão recriados — `
      + 'pedido de mensalidade nunca é copiado no cancelamento, e a mensalidade correspondente será '
      + 'cancelada junto. Para voltar a cobrar, a mensalidade precisa ser gerada novamente.';
  }

  isTuition(order: CancelInvoicePreviewOrder): boolean {
    return (order.origin || '').toUpperCase() === 'TUITION';
  }

  notRecreatedReason(order: CancelInvoicePreviewOrder): string {
    return this.isTuition(order)
      ? 'Pedido de mensalidade não é recriado — a mensalidade é cancelada junto.'
      : 'Este pedido já está cancelado, por isso não gera cópia.';
  }

  blockReasonLabel(reason: InvoiceCancelBlockReason): string {
    return CancelBatchDialogComponent.BLOCK_REASON_LABEL[reason] || reason;
  }

  blockReasonsLabel(invoice: CancelInvoicePreviewInvoice): string {
    return (invoice.blockReasons || []).map((r) => this.blockReasonLabel(r)).join(' · ');
  }

  quotasLabel(customerNames: { customerName: string }[]): string {
    return (customerNames || []).map((q) => q.customerName || '—').join(', ');
  }

  confirm(): void {
    if (!this.confirmationEnabled) {
      return;
    }
    const invoiceIds = this.cancelableGroups
      .reduce((ids, group) => ids.concat(group.invoices.map((i) => i.invoiceId)), [] as number[]);
    this.dialogRef.close({ confirmed: true, invoiceIds });
  }

  close(): void {
    this.dialogRef.close(null);
  }
}
